import React from 'react';
import styled from '@emotion/styled';
import { Button, Text } from '../../common';

const WelcomeScreenContainer = styled.div`
  min-height: 100%;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
  padding: 0 20px;

  @media (max-width: 768px) {
    margin-top: 20px;
    gap: 16px;
    padding: 0 12px;
  }
`;

const WelcomeHeading = styled.h1`
  font-size: 24px;
  margin-bottom: 3px;
  margin-top: 45px;
  color: #333;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 6px;
  }
`;

const WelcomeText = styled.p`
  margin: 1px 0;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 13px;
    margin: 1px 0;
    line-height: 1.4;
  }
`;

const WelcomeSection = styled.p`
  margin-bottom: 6px;
  font-size: 16px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 6px;
    line-height: 1.4;
  }
`;

const ReadinessSection = styled.div`
  margin: 1px 0;

  @media (max-width: 768px) {
    margin: 1px 0;
  }
`;

const ReadinessHeading = styled.h2`
  font-size: 18px;
  margin-bottom: 5px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 8px;
  }
`;

const CheckboxContainer = styled.div`
  margin-bottom: 13px;

  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 14px;
    gap: 8px;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
  }
`;

const WelcomeButton = styled(Button)`
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 15px;
    max-width: 180px;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ScreenerSection = styled.div`
  margin: 11px 0 10px 0;

  @media (max-width: 768px) {
    margin: 11px 0 8px 0;
  }
`;

const ScreenerHeading = styled.h3`
  font-size: 16px;
  margin-bottom: 5px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 4px;
  }
`;

const ScreenerSubtext = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 0;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const WelcomeScreen = ({ 
  projectName, 
  deviceReady, 
  internetReady, 
  onDeviceReadyChange, 
  onInternetReadyChange, 
  onNext 
}) => {
  return (
    <WelcomeScreenContainer>
      <WelcomeContent>
        <WelcomeHeading>Welcome to {projectName}!</WelcomeHeading>
        <WelcomeSection>
          Please answer a few simple questions to give us some background on your experience and usage.
        </WelcomeSection>

        <ReadinessSection>
          <ReadinessHeading>Let's make sure you're ready</ReadinessHeading>

          <CheckboxContainer>
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                checked={deviceReady}
                onChange={e => onDeviceReadyChange(e.target.checked)}
                className="green-checkbox"
              />
              My device is ready to use
            </CheckboxLabel>
          </CheckboxContainer>

          <CheckboxContainer>
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                checked={internetReady}
                onChange={e => onInternetReadyChange(e.target.checked)}
                className="green-checkbox"
              />
              My internet connection is stable
            </CheckboxLabel>
          </CheckboxContainer>
        </ReadinessSection>

        <WelcomeText>
          We want to know your opinion. That means there are no wrong answers. Once you're done, you'll be able to
          start your next task.
        </WelcomeText>

        <WelcomeText>
          As mentioned in the terms and conditions, you are giving us permission to record your response for
          research purposes
        </WelcomeText>

        <ScreenerSection>
          <ScreenerHeading>Let's get you started!</ScreenerHeading>
          <ScreenerSubtext>Your opinion helps us build better experiences for everyone</ScreenerSubtext>
        </ScreenerSection>

        <WelcomeButton
          primary
          onClick={onNext}
          disabled={!deviceReady || !internetReady}
          className="green-button"
        >
          Next
        </WelcomeButton>
      </WelcomeContent>
    </WelcomeScreenContainer>
  );
};

export default WelcomeScreen;